import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Clock, AlertCircle } from 'lucide-react';

// Mirrors backend POST_EDIT_WINDOW_MINUTES in routers/posts.py. Kept in a
// single module-level constant so the countdown formula and the "expired"
// check use the same source of truth.
const EDIT_WINDOW_MINUTES = 15;

function fmtCountdown(seconds) {
  if (seconds <= 0) return 'expired';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Phase 24 — Post editor modal.
 *
 * Surfaces the three editable fields (text, public hashtags, private account
 * tags) alongside a live countdown until the 15-minute edit window expires.
 * Hashtags and private tags accept comma/space-separated strings and are
 * normalized client-side before submission; the server does the
 * authoritative normalization, so the UX just keeps the user from sending
 * obvious garbage.
 */
export default function EditPostDialog({ open, post, onClose, onSaved }) {
  const [text, setText] = useState('');
  const [hashtagsInput, setHashtagsInput] = useState('');
  const [privateTagsInput, setPrivateTagsInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Seed the form every time a new post is opened. We deliberately re-seed
  // when `open` flips true so reusing the dialog for multiple posts in a
  // session doesn't show stale fields from the last edit.
  useEffect(() => {
    if (!open || !post) return;
    setText(post.text || '');
    setHashtagsInput((post.hashtags || []).map((h) => `#${h}`).join(' '));
    // Private account tags are usernames. Server-hydrated payloads don't
    // include them in public feeds (for privacy), so we try to pull them
    // from `post.private_account_tags` when present and fall back to empty.
    // The edit endpoint resolves usernames server-side anyway.
    const priv = post.private_account_tags || post.private_tag_usernames || [];
    setPrivateTagsInput(priv.map((u) => (u.startsWith('@') ? u : `@${u}`)).join(' '));
  }, [open, post]);

  // Tick the countdown once per second while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const { secondsLeft, expired } = useMemo(() => {
    if (!post?.created_at) return { secondsLeft: 0, expired: true };
    const created = new Date(post.created_at).getTime();
    const windowEnd = created + EDIT_WINDOW_MINUTES * 60 * 1000;
    const left = Math.max(0, Math.floor((windowEnd - now) / 1000));
    return { secondsLeft: left, expired: left <= 0 };
  }, [post?.created_at, now]);

  const submit = async () => {
    if (!post?.id || expired) return;
    setSaving(true);
    try {
      // Normalize client-side: strip leading '#' / '@', split on whitespace/commas,
      // drop empties, de-dupe case-insensitively. Server will re-normalize
      // authoritatively — this is only to keep the payload reasonable.
      const hashtags = Array.from(new Set(
        (hashtagsInput || '')
          .split(/[\s,]+/)
          .map((t) => t.replace(/^#/, '').trim().toLowerCase())
          .filter(Boolean),
      ));
      const privateAccountTags = Array.from(new Set(
        (privateTagsInput || '')
          .split(/[\s,]+/)
          .map((t) => t.replace(/^@/, '').trim().toLowerCase())
          .filter(Boolean),
      ));
      const { data: updated } = await api.patch(`/posts/${post.id}`, {
        text,
        hashtags,
        private_account_tags: privateAccountTags,
      });
      toast.success('Post updated');
      if (onSaved) onSaved(updated);
      if (onClose) onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to edit post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && onClose) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-post-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Edit post</DialogTitle>
          <DialogDescription>
            You can edit the text, public hashtags, and private account tags
            within the first {EDIT_WINDOW_MINUTES} minutes of posting. Media,
            feed targets, and location cannot be edited.
          </DialogDescription>
        </DialogHeader>

        {/* Countdown strip */}
        <div
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            expired
              ? 'border-[hsl(var(--destructive)/0.5)] bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive-foreground))]'
              : 'border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] text-muted-foreground'
          }`}
          data-testid="edit-post-countdown"
        >
          {expired ? <AlertCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
          {expired
            ? 'Edit window expired. You can no longer edit this post.'
            : <>Edit window closes in <span className="ml-1 font-mono tabular-nums text-foreground">{fmtCountdown(secondsLeft)}</span></>
          }
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="edit-post-text">Text</Label>
            <Textarea
              id="edit-post-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              disabled={expired || saving}
              maxLength={10000}
              data-testid="edit-post-text"
              placeholder="What are you sharing?"
            />
          </div>

          <div>
            <Label htmlFor="edit-post-hashtags">Hashtags</Label>
            <Input
              id="edit-post-hashtags"
              value={hashtagsInput}
              onChange={(e) => setHashtagsInput(e.target.value)}
              disabled={expired || saving}
              placeholder="#realtime #creators"
              data-testid="edit-post-hashtags"
            />
            <p className="mt-1 font-mono text-[11px] text-muted-foreground">
              Space or comma separated. Leading # is optional.
            </p>
          </div>

          <div>
            <Label htmlFor="edit-post-private-tags">Private account tags</Label>
            <Input
              id="edit-post-private-tags"
              value={privateTagsInput}
              onChange={(e) => setPrivateTagsInput(e.target.value)}
              disabled={expired || saving}
              placeholder="@alex @marie"
              data-testid="edit-post-private-tags"
            />
            <p className="mt-1 font-mono text-[11px] text-muted-foreground">
              Privately notifies these users. Never shown publicly.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="edit-post-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={expired || saving}
            data-testid="edit-post-save"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
